import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { Spin } from '@pepper/ui'

import { formatLongNumber } from 'utils/functions/formatLongNumber'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useRoi } from 'container/SEOTools/hooks/useRoi'
import { useRoiIntervals } from 'container/SEOTools/hooks/useRoiIntervals'
import { useGetDomainRefreshParams } from 'container/SEOTools/hooks/useGetDomainRefreshParams'
import { StatsBox } from '../Stats/StatsBox'

import styles from './CmpStats.module.css'

type Props = {
  selectedDomainId: string
  width: 'full' | 'half'
}

export const CmpStats = ({ selectedDomainId, width }: Props) => {
  const companyId = useSelectedCompanyId()
  const { data: intervalsData } = useRoiIntervals({ companyId })

  const [statsResponse, analyticsResponse] = useRoi({
    companyId,
    domainId: selectedDomainId,
    date: intervalsData?.data?.monthly[0]?.start_date ?? '',
    period: 'monthly',
    isEnabled:
      !!selectedDomainId && !!intervalsData?.data?.monthly?.[0]?.start_date,
  })

  const { data: domainRefreshStats, isLoading } = useGetDomainRefreshParams({
    companyId,
    domainId: selectedDomainId,
    isEnabled: !!companyId && !!selectedDomainId,
  })

  const analyticsTraffic = analyticsResponse?.data?.data?.aggregate?.traffic
  const analyticsRankingKeywords =
    analyticsResponse?.data?.data?.aggregate?.ranking_keywords
  const statsTraffic = statsResponse?.data?.data.traffic

  const isProcessingData =
    (!statsResponse.isLoading &&
      statsResponse.data?.report_status === 'processing') ||
    (!analyticsResponse.isLoading &&
      analyticsResponse.data?.report_status === 'processing')

  return (
    <div className={styles.container}>
      {isProcessingData ? (
        <div className={styles.processing}>
          <div className={styles.processingHeading}>
            <Spin size="small" />
            <h3 className={styles.processingTitle}>
              Processing relavant data...
            </h3>
          </div>
          <p className={styles.processingDesc}>
            This may take a few mins.
            <br />
            Explore features by the time we start populating the data.
          </p>
        </div>
      ) : (
        <div className={styles.stats}>
          {width === 'full' && (
            <StatsBox
              title="Total Traffic"
              value={formatLongNumber(analyticsTraffic?.value ?? 0, 1)}
              percentChange={analyticsTraffic?.growth_percentage}
              isLoading={analyticsResponse.isLoading}
            />
          )}

          <StatsBox
            title="Org Search Traffic"
            value={formatLongNumber(
              analyticsTraffic?.breakdown[1]?.value ?? 0,
              1
            )}
            percentChange={analyticsTraffic?.breakdown[1].growth_percentage}
            isLoading={analyticsResponse.isLoading}
          />

          <StatsBox
            title="Org Search Value"
            value={
              statsTraffic ? `$${formatLongNumber(statsTraffic.value, 1)}` : '-'
            }
            percentChange={statsTraffic?.growth_percentage}
            isLoading={statsResponse.isLoading}
          />

          {width === 'full' && (
            <StatsBox
              title="Total Ranking Keywors"
              value={formatLongNumber(analyticsRankingKeywords?.value ?? 0, 1)}
              percentChange={analyticsRankingKeywords?.growth_percentage}
              isLoading={analyticsResponse.isLoading}
            />
          )}
        </div>
      )}
      <footer className={styles.footer}>
        {!!domainRefreshStats && !isLoading ? (
          <>
            <span className={styles.footerText}>
              <Icon icon="lucide:sparkles" />
              Gain {formatLongNumber(
                domainRefreshStats?.potential_traffic
              )}{' '}
              potential traffic
            </span>
            <Link className={styles.footerLink} to="/content-refresh">
              View Opportunities <Icon icon="lucide:arrow-right" />
            </Link>
          </>
        ) : isProcessingData ? (
          <span>Analyzing opportunities...</span>
        ) : null}
      </footer>
    </div>
  )
}
